// Circular player avatar with optional crown overlay. Wraps renderHeadIcon
// (the same WebGPU head renderer used by the round-end modal) and adds a
// chunky white frame matching the design mockups. The crown overlay points
// at /ui/onlineplay/party-owner-circle.svg and is rendered as an absolute
// positioned <img> on top of the head canvas.

import { renderHeadIcon } from '../HeadIcon';
import type { HatId } from '../../render/HatModels';

const STYLES_ID = 'avatar-circle-styles';
const STYLES = `
.avatar-circle {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
}
.avatar-circle .frame {
  position: absolute; inset: 0;
  border-radius: 50%;
  background: #FFFFFF;
  box-shadow: 0 4px 10px rgba(0,0,0,0.22), inset 0 -3px 6px rgba(0,0,0,0.12);
}
.avatar-circle .head-clip {
  position: absolute; inset: 6%;
  border-radius: 50%;
  overflow: hidden;
  background: #BFD8E2;
}
.avatar-circle .head-clip canvas,
.avatar-circle .head-clip img.head-img {
  width: 100%; height: 100%;
  object-fit: cover;
  object-position: center 20%;
  display: block;
}
.avatar-circle .crown {
  position: absolute;
  top: -10%; right: -10%;
  width: 42%;
  height: 42%;
  pointer-events: none;
}
.avatar-circle .crown img {
  width: 100%; height: 100%;
  display: block;
}
`;

export interface AvatarCircleOptions {
  color: string;
  hatId: HatId | null;
  /** Pixel size of the outer circle. Inner head canvas is rendered to a
   *  larger square (size * 1.6) so the head fills the circular crop with
   *  margin to spare. */
  size: number;
  withCrown?: boolean;
}

/** Synchronously returns an `.avatar-circle` element. The head canvas is
 *  rendered async — the element shows a flat color fill until the head
 *  arrives, then the canvas is swapped in. Returns the root element so
 *  the caller can position it freely. */
export function makeAvatarCircle(opts: AvatarCircleOptions): HTMLElement {
  ensureStyles();
  const root = document.createElement('div');
  root.className = 'avatar-circle';
  root.style.width = `${opts.size}px`;
  root.style.height = `${opts.size}px`;
  root.innerHTML = `
    <div class="frame"></div>
    <div class="head-clip" style="background:${escapeAttr(opts.color)};"></div>
    ${opts.withCrown ? `<div class="crown"><img src="/ui/onlineplay/party-owner-circle.svg" alt="" /></div>` : ''}
  `;
  // Render the head icon offscreen and swap it in when ready.
  const clip = root.querySelector('.head-clip') as HTMLElement;
  const renderSize = Math.max(64, Math.round(opts.size * 1.6));
  renderHeadIcon({ color: opts.color, hatId: opts.hatId, size: renderSize })
    .then((canvas) => {
      if (!clip.isConnected) return;
      clip.innerHTML = '';
      clip.appendChild(canvas);
      clip.style.background = 'transparent';
    })
    .catch(() => { /* keep flat color fallback */ });
  return root;
}

function ensureStyles(): void {
  if (document.getElementById(STYLES_ID)) return;
  const style = document.createElement('style');
  style.id = STYLES_ID;
  style.textContent = STYLES;
  document.head.appendChild(style);
}

function escapeAttr(s: string): string {
  return s.replace(/["<>&]/g, (c) => ({ '"': '&quot;', '<': '&lt;', '>': '&gt;', '&': '&amp;' })[c]!);
}
